import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  Collapse,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// Task structure: recurrencePattern is full object when recurring
// recurrencePattern: { isRecurring, frequency, interval, specificTimes, daysOfWeek, dayOfMonth, startDateTime, endCondition }

const FREQUENCY_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

const END_CONDITION_TYPES = [
  { value: "never", label: "Never" },
  { value: "count", label: "After count" },
  { value: "untilDate", label: "Until date" },
  { value: "duration", label: "Duration" },
];

const DURATION_UNITS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

const DAYS_OF_WEEK = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

const getDefaultEndCondition = () => ({
  type: "never",
  count: null,
  untilDateTime: null,
  duration: { value: null, unit: "days" },
});

const getDefaultRecurrencePattern = () => ({
  isRecurring: false,
  frequency: "daily",
  interval: 1,
  specificTimes: [],
  daysOfWeek: [],
  dayOfMonth: null,
  startDateTime: "",
  endCondition: getDefaultEndCondition(),
});

const getDefaultTask = () => ({
  id: crypto.randomUUID?.() || Date.now().toString(36),
  taskTitle: "",
  description: "",
  priority: "Medium",
  dueDate: "",
  dueTime: "",
  assignedTo: "",
  category: "",
  status: "Pending",
  createdAt: new Date().toISOString(),
  recurrencePattern: getDefaultRecurrencePattern(),
});

// Parse "06:00, 12:00" to ["06:00", "12:00"]; serialize back for input
const parseSpecificTimes = (str) =>
  (str || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
const formatSpecificTimes = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

// Human-readable recurrence summary for list display (safe for any shape of rp)
const formatRecurrenceLabel = (rp) => {
  if (!rp || !rp.isRecurring) return "";
  try {
    const { frequency, interval, specificTimes, daysOfWeek, dayOfMonth, endCondition } = rp || {};
    const intervalStr = (interval > 1 ? `every ${interval} ` : "");
    let freqStr = "";
    if (frequency === "hourly") freqStr = `${intervalStr}hour(s)`;
    else if (frequency === "daily") {
      const times = Array.isArray(specificTimes) ? specificTimes.join(", ") : "";
      freqStr = times ? `daily at ${times}` : `${intervalStr}day(s)`;
    } else if (frequency === "weekly") {
      const days = Array.isArray(daysOfWeek) && daysOfWeek.length ? daysOfWeek.join(", ") : "week";
      freqStr = `${intervalStr}week(s)${days !== "week" ? ` (${days})` : ""}`;
    } else if (frequency === "monthly") {
      freqStr = dayOfMonth ? `monthly on day ${dayOfMonth}` : `${intervalStr}month(s)`;
    } else freqStr = `${intervalStr}${frequency || ""}`;
    let endStr = "";
    if (endCondition?.type === "count" && endCondition?.count != null) endStr = `, ${endCondition.count} times`;
    else if (endCondition?.type === "untilDate" && endCondition?.untilDateTime) endStr = ` until ${String(endCondition.untilDateTime).slice(0, 10)}`;
    else if (endCondition?.type === "duration" && endCondition?.duration?.value) endStr = ` for ${endCondition.duration.value} ${endCondition.duration?.unit || "days"}`;
    return `Repeats ${freqStr}${endStr}`;
  } catch {
    return "";
  }
};

const TaskPlansModal = ({
  open,
  onClose,
  patientId,
  patientName,
  initialTasks = [],
  onSaveTasks,
}) => {
  const [tasks, setTasks] = useState([]);
  const [formData, setFormData] = useState(getDefaultTask());
  const [descriptionDialog, setDescriptionDialog] = useState({ open: false, text: "" });
  const isEditing = tasks.some((t) => t.id === formData.id);

  useEffect(() => {
    if (open) {
      setTasks(Array.isArray(initialTasks) ? [...initialTasks] : []);
      setFormData(getDefaultTask());
    }
  }, [open, initialTasks]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRecurrenceChange = (path, value) => {
    setFormData((prev) => {
      const rp = { ...(prev.recurrencePattern || getDefaultRecurrencePattern()) };
      if (path === "isRecurring") rp.isRecurring = value;
      else if (path === "frequency") rp.frequency = value;
      else if (path === "interval") rp.interval = value === "" ? 1 : Math.max(1, parseInt(value, 10) || 1);
      else if (path === "specificTimes") rp.specificTimes = value;
      else if (path === "daysOfWeek") rp.daysOfWeek = value;
      else if (path === "dayOfMonth") rp.dayOfMonth = value === "" || value === null ? null : parseInt(value, 10);
      else if (path === "startDateTime") rp.startDateTime = value;
      else if (path.startsWith("endCondition.")) {
        const key = path.replace("endCondition.", "");
        rp.endCondition = { ...(rp.endCondition || getDefaultEndCondition()) };
        if (key === "type") rp.endCondition.type = value;
        else if (key === "count") rp.endCondition.count = value === "" ? null : parseInt(value, 10);
        else if (key === "untilDateTime") rp.endCondition.untilDateTime = value || null;
        else if (key === "duration.value") rp.endCondition.duration = { ...rp.endCondition.duration, value: value === "" ? null : parseInt(value, 10) };
        else if (key === "duration.unit") rp.endCondition.duration = { ...rp.endCondition.duration, unit: value };
      }
      return { ...prev, recurrencePattern: rp };
    });
  };

  const toggleDayOfWeek = (day) => {
    setFormData((prev) => {
      const rp = { ...(prev.recurrencePattern || getDefaultRecurrencePattern()) };
      const days = rp.daysOfWeek || [];
      const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
      rp.daysOfWeek = next;
      return { ...prev, recurrencePattern: rp };
    });
  };

  const buildStartDateTime = () => {
    const { dueDate, dueTime, recurrencePattern } = formData;
    if (recurrencePattern?.startDateTime) return recurrencePattern.startDateTime;
    if (dueDate && dueTime) return `${dueDate}T${dueTime}:00Z`;
    if (dueDate) return `${dueDate}T00:00:00Z`;
    return new Date().toISOString().slice(0, 19) + "Z";
  };

  const handleEditTask = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const rp = task.recurrencePattern || {};
    setFormData({
      ...task,
      recurrencePattern: {
        ...getDefaultRecurrencePattern(),
        ...rp,
        isRecurring: Boolean(rp.isRecurring),
        endCondition: { ...getDefaultEndCondition(), ...(rp.endCondition || {}) },
      },
    });
  };

  const handleCancelEdit = () => {
    setFormData(getDefaultTask());
  };

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!formData.taskTitle?.trim()) return;
    const rp = formData.recurrencePattern || getDefaultRecurrencePattern();
    const recurrencePattern = rp.isRecurring
      ? {
        isRecurring: true,
        frequency: rp.frequency || "daily",
        interval: rp.interval ?? 1,
        specificTimes: Array.isArray(rp.specificTimes) ? rp.specificTimes : [],
        daysOfWeek: Array.isArray(rp.daysOfWeek) ? rp.daysOfWeek : [],
        dayOfMonth: rp.dayOfMonth ?? null,
        startDateTime: rp.startDateTime || buildStartDateTime(),
        endCondition: {
          type: rp.endCondition?.type || "never",
          count: rp.endCondition?.type === "count" ? rp.endCondition?.count ?? null : null,
          untilDateTime: rp.endCondition?.type === "untilDate" ? rp.endCondition?.untilDateTime ?? null : null,
          duration:
            rp.endCondition?.type === "duration" && rp.endCondition?.duration
              ? { value: rp.endCondition.duration.value ?? null, unit: rp.endCondition.duration.unit || "days" }
              : { value: null, unit: "days" },
        },
      }
      : { ...getDefaultRecurrencePattern(), isRecurring: false };

    const payload = {
      ...formData,
      id: formData.id || crypto.randomUUID?.() || Date.now().toString(36),
      taskTitle: formData.taskTitle.trim(),
      description: formData.description?.trim() || "",
      createdAt: formData.createdAt || new Date().toISOString(),
      recurrencePattern,
    };

    if (isEditing) {
      setTasks((prev) => {
        const next = prev.map((t) => (t.id === formData.id ? { ...payload, status: t.status } : t));
        if (onSaveTasks) onSaveTasks(next);
        return next;
      });
    } else {
      setTasks((prev) => {
        const newTask = { ...payload, status: "Pending" };
        const next = [...prev, newTask];
        if (onSaveTasks) onSaveTasks(next);
        return next;
      });
    }
    setFormData(getDefaultTask());
  };

  const handleToggleStatus = (taskId) => {
    setTasks((prev) => {
      const next = prev.map((t) =>
        t.id === taskId
          ? {
            ...t,
            status: t.status === "Done" ? "Pending" : "Done",
          }
          : t
      );
      if (onSaveTasks) onSaveTasks(next);
      return next;
    });
  };

  const handleDeleteTask = (taskId) => {
    setTasks((prev) => {
      const next = prev.filter((t) => t.id !== taskId);
      if (onSaveTasks) onSaveTasks(next);
      return next;
    });
  };

  const handleClose = () => {
    if (onSaveTasks) onSaveTasks(tasks);
    onClose();
  };

  const priorityColor = (p) =>
    p === "High" ? "error" : p === "Medium" ? "warning" : "default";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
        <AssignmentIcon />
        Task Plans {patientName ? `– ${patientName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {/* Form at top */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {isEditing ? "Edit task" : "Add task for nursing unit"}
        </Typography>
        <form onSubmit={handleAddTask}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Task title"
              required
              placeholder="e.g. Vital signs check, Medication round"
              value={formData.taskTitle}
              onChange={handleChange("taskTitle")}
            />
            <TextField
              fullWidth
              size="small"
              label="Description (optional)"
              multiline
              rows={2}
              placeholder="Details for the nurse"
              value={formData.description}
              onChange={handleChange("description")}
            />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={formData.priority}
                  onChange={handleChange("priority")}
                >
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Due date"
                InputLabelProps={{ shrink: true }}
                value={formData.dueDate}
                onChange={handleChange("dueDate")}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small"
                type="time"
                label="Due time"
                InputLabelProps={{ shrink: true }}
                value={formData.dueTime}
                onChange={handleChange("dueTime")}
                sx={{ minWidth: 100 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Assigned to (optional)"
                placeholder="Nurse / Unit"
                value={formData.assignedTo}
                onChange={handleChange("assignedTo")}
                sx={{ flex: 1, minWidth: 140 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={formData.category || ""}
                  onChange={handleChange("category")}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Medicine">Medicine</MenuItem>
                  <MenuItem value="Vitals">Vitals</MenuItem>
                  <MenuItem value="Labs">Labs</MenuItem>
                  <MenuItem value="Rounds">Rounds</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.recurrencePattern?.isRecurring)}
                    onChange={(e) => handleRecurrenceChange("isRecurring", e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Recurring task</Typography>}
              />
            </Box>
            <Collapse in={formData.recurrencePattern?.isRecurring}>
              <Box sx={{ pl: 0, display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      label="Frequency"
                      value={formData.recurrencePattern?.frequency || "daily"}
                      onChange={(e) => handleRecurrenceChange("frequency", e.target.value)}
                    >
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    label="Interval"
                    helperText="e.g. every 6 hours"
                    value={formData.recurrencePattern?.interval ?? 1}
                    onChange={(e) => handleRecurrenceChange("interval", e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 90 }}
                  />
                </Box>
                {(formData.recurrencePattern?.frequency === "daily" || formData.recurrencePattern?.frequency === "custom") && (
                  <TextField
                    size="small"
                    label="Specific times (optional)"
                    placeholder="06:00, 12:00, 18:00"
                    value={formatSpecificTimes(formData.recurrencePattern?.specificTimes)}
                    onChange={(e) => handleRecurrenceChange("specificTimes", parseSpecificTimes(e.target.value))}
                  />
                )}
                {formData.recurrencePattern?.frequency === "weekly" && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Days of week
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {DAYS_OF_WEEK.map((day) => (
                        <FormControlLabel
                          key={day}
                          control={
                            <Checkbox
                              size="small"
                              checked={(formData.recurrencePattern?.daysOfWeek || []).includes(day)}
                              onChange={() => toggleDayOfWeek(day)}
                            />
                          }
                          label={<Typography variant="caption">{day}</Typography>}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {formData.recurrencePattern?.frequency === "monthly" && (
                  <TextField
                    size="small"
                    type="number"
                    label="Day of month (1-31)"
                    value={formData.recurrencePattern?.dayOfMonth ?? ""}
                    onChange={(e) => handleRecurrenceChange("dayOfMonth", e.target.value)}
                    inputProps={{ min: 1, max: 31 }}
                    sx={{ width: 140 }}
                  />
                )}
                <TextField
                  size="small"
                  label="Start date/time (optional)"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={
                    formData.recurrencePattern?.startDateTime
                      ? formData.recurrencePattern.startDateTime.slice(0, 16)
                      : ""
                  }
                  onChange={(e) => handleRecurrenceChange("startDateTime", e.target.value ? `${e.target.value}:00Z` : "")}
                  sx={{ maxWidth: 260 }}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    End condition
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={formData.recurrencePattern?.endCondition?.type || "never"}
                        onChange={(e) => handleRecurrenceChange("endCondition.type", e.target.value)}
                      >
                        {END_CONDITION_TYPES.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {formData.recurrencePattern?.endCondition?.type === "count" && (
                      <TextField
                        size="small"
                        type="number"
                        label="Count"
                        value={formData.recurrencePattern?.endCondition?.count ?? ""}
                        onChange={(e) => handleRecurrenceChange("endCondition.count", e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: 90 }}
                      />
                    )}
                    {formData.recurrencePattern?.endCondition?.type === "untilDate" && (
                      <TextField
                        size="small"
                        type="datetime-local"
                        label="Until"
                        InputLabelProps={{ shrink: true }}
                        value={
                          formData.recurrencePattern?.endCondition?.untilDateTime
                            ? formData.recurrencePattern.endCondition.untilDateTime.slice(0, 16)
                            : ""
                        }
                        onChange={(e) =>
                          handleRecurrenceChange("endCondition.untilDateTime", e.target.value ? `${e.target.value}:00Z` : null)
                        }
                        sx={{ minWidth: 200 }}
                      />
                    )}
                    {formData.recurrencePattern?.endCondition?.type === "duration" && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          size="small"
                          type="number"
                          label="Value"
                          value={formData.recurrencePattern?.endCondition?.duration?.value ?? ""}
                          onChange={(e) => handleRecurrenceChange("endCondition.duration.value", e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 80 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            label="Unit"
                            value={formData.recurrencePattern?.endCondition?.duration?.unit || "days"}
                            onChange={(e) => handleRecurrenceChange("endCondition.duration.unit", e.target.value)}
                          >
                            {DURATION_UNITS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Collapse>
            <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-start" }}>
              <Button type="submit" variant="contained" size="small">
                {isEditing ? "Update task" : "Add task"}
              </Button>
              {isEditing && (
                <Button type="button" variant="outlined" size="small" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </form>

        <Divider sx={{ my: 2 }} />

        {/* Task list at bottom - Show all tasks (recurring and non-recurring) */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Task list ({tasks.length})
        </Typography>
        {tasks.length > 0 && (
          <Box sx={{ height: 280, width: "100%" }}>
            <DataGrid
              rows={tasks.map((t) => {
                const recurrenceStr =
                  t.recurrencePattern?.isRecurring || t.isRecurring
                    ? typeof t.recurrencePattern === "object" && t.recurrencePattern?.isRecurring
                      ? formatRecurrenceLabel(t.recurrencePattern)
                      : typeof t.recurrencePattern === "string" ? `Repeats ${t.recurrencePattern}` : ""
                    : "";
                return {
                  id: t.id,
                  taskTitle: String(t.taskTitle ?? ""),
                  description: String(t.description ?? ""),
                  priority: t.priority === "High" || t.priority === "Medium" || t.priority === "Low" ? t.priority : "Medium",
                  due: t.dueDate ? (t.dueTime ? `${t.dueDate} ${t.dueTime}` : String(t.dueDate)) : "",
                  assignedTo: String(t.assignedTo ?? ""),
                  status: t.status === "Done" ? "Done" : "Pending",
                  recurrence: recurrenceStr,
                };
              })}
              getRowId={(row) => row.id}
              columns={[
                {
                  field: "status",
                  headerName: "",
                  width: 48,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  renderCell: (params) => (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(params.row.id);
                      }}
                      color={params.row.status === "Done" ? "success" : "default"}
                    >
                      {params.row.status === "Done" ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" />
                      )}
                    </IconButton>
                  ),
                },
                {
                  field: "taskTitle",
                  headerName: "Task",
                  flex: 1,
                  minWidth: 120,
                  renderCell: (params) => (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        textDecoration: params.row.status === "Done" ? "line-through" : "none",
                        opacity: params.row.status === "Done" ? 0.8 : 1,
                        py: 0.5,
                      }}
                    >
                      {params.row.taskTitle}
                    </Typography>
                  ),
                },
                {
                  field: "description",
                  headerName: "Description",
                  flex: 1,
                  minWidth: 120,
                  renderCell: (params) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDescriptionDialog({ open: true, text: params.row.description || "" });
                        }}
                        title="View description"
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textDecoration: params.row.status === "Done" ? "line-through" : "none",
                          opacity: params.row.status === "Done" ? 0.8 : 1,
                        }}
                      >
                        {params.row.description || "—"}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  field: "priority",
                  headerName: "Priority",
                  width: 100,
                  renderCell: (params) => (
                    <Chip size="small" label={params.value || "Medium"} color={priorityColor(params.value || "Medium")} />
                  ),
                },
                {
                  field: "due",
                  headerName: "Due",
                  width: 180,
                  valueGetter: (params) => params.row?.due ?? "",
                },
                {
                  field: "assignedTo",
                  headerName: "Assigned to",
                  width: 110,
                },
                {
                  field: "recurrence",
                  headerName: "Recurrence",
                  width: 140,
                  renderCell: (params) =>
                    params.value ? (
                      <Chip size="small" label={String(params.value)} variant="outlined" sx={{ fontSize: "0.7rem", maxWidth: 130 }} />
                    ) : null,
                },
                {
                  field: "actions",
                  headerName: "Action",
                  width: 100,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  renderCell: (params) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTask(params.row.id);
                        }}
                        title="Edit task"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTask(params.row.id);
                        }}
                        title="Delete task"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ),
                },
              ]}
              getRowClassName={(params) => (params.row.status === "Done" ? "task-done" : "")}
              disableRowSelectionOnClick
              hideFooter
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  alignItems: "flex-start",
                  paddingTop: 1,
                  paddingBottom: 1,
                },
                "& .MuiDataGrid-cellContent": { overflow: "visible", whiteSpace: "normal" },
                "& .MuiDataGrid-row": { minHeight: "52px !important", maxHeight: "none !important" },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  minHeight: "40px !important",
                  maxHeight: "40px !important",
                },
              }}
            />
          </Box>
        )}
        {tasks.length === 0 && (
          <Box
            sx={{
              height: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.50",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No tasks yet. Add tasks above for the nursing unit.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>

      <Dialog
        open={descriptionDialog.open}
        onClose={() => setDescriptionDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Description</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {descriptionDialog.text || "No description."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionDialog((prev) => ({ ...prev, open: false }))} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TaskPlansModal;

